import React from 'react';
import {
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Button,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { makeStyles } from '@mui/styles';
import { renderTextField, renderDropdown } from 'components/form/field/redux-field';
import { Field } from 'redux-form';
import { Close as CloseIcon } from '@mui/icons-material';
import { cpStyle } from '../../index.styles';
import { IFields } from '../../table/types';
import {
  SITE_DROPDOWN,
  FOUNDATIONS_DROPDOWN,
  SUPPLY_ORDER_DROPDOWN,
  loginsAccountsPrefixEnum,
  LaDynamicPairFieldsEnum,
} from '../../constants';

interface ISubFormProps {
  formTitle: string;
  fieldsInfo: IFields[];
  setFieldValue: (field: string, value: any) => void;
  updateDynamicSiteList: (siteList: { id: number }[]) => void;
  updateDynamicFoundationList: (foundationList: { id: number }[]) => void;
  updateDynamicSupplyOrderList: (supplyOrderList: { id: number }[]) => void;
  updateSiteIdMapper: (siteMapper: { dbId: number; counter: number }[]) => void;
  updateFoundationsIdMapper: (foundationsMapper: { dbId: number; counter: number }[]) => void;
  updateSupplyOrderIdMapper: (supplyOrderMapper: { dbId: number; counter: number }[]) => void;
}

export const LoginsAccounts: React.FC<ISubFormProps> = (props: ISubFormProps): JSX.Element => {
  const useStyles = makeStyles(cpStyle);
  const classes = useStyles();
  const MAX_LENGTH_INPUT = '100';

  const {
    formTitle,
    fieldsInfo,
    setFieldValue,
    updateDynamicSiteList,
    updateDynamicFoundationList,
    updateDynamicSupplyOrderList,
    updateSiteIdMapper,
    updateFoundationsIdMapper,
    updateSupplyOrderIdMapper,
  } = props;

  const [dynamicSiteList, setDynamicSiteList] = React.useState<any[]>([]);
  const [dynamicFoundationList, setDynamicFoundationList] = React.useState<any[]>([]);
  const [dynamicSupplyOrderList, setDynamicSupplyOrderList] = React.useState<any[]>([]);

  const addDynamicSite = () => {
    const newId = dynamicSiteList.length > 0 ? dynamicSiteList.slice(-1)[0].id + 1 : 1;
    setDynamicSiteList((dynamicSiteList: any) => {
      return [
        ...dynamicSiteList,
        {
          id: newId,
        },
      ];
    });
  };

  const deleteDynamicSite = (id: number) => {
    setDynamicSiteList((dynamicSiteList: any) => dynamicSiteList.filter((x: any) => x.id !== id));
  };

  const addDynamicFoundation = () => {
    const newId = dynamicFoundationList.length > 0 ? dynamicFoundationList.slice(-1)[0].id + 1 : 1;
    setDynamicFoundationList((dynamicFoundationList: any) => {
      return [
        ...dynamicFoundationList,
        {
          id: newId,
        },
      ];
    });
  };

  const deleteDynamicFoundation = (id: number) => {
    setDynamicFoundationList((dynamicFoundationList: any) =>
      dynamicFoundationList.filter((x: any) => x.id !== id),
    );
  };

  const addDynamicSupplyOrder = () => {
    const newId =
      dynamicSupplyOrderList.length > 0 ? dynamicSupplyOrderList.slice(-1)[0].id + 1 : 1;
    setDynamicSupplyOrderList((dynamicSupplyOrderList: any) => {
      return [
        ...dynamicSupplyOrderList,
        {
          id: newId,
        },
      ];
    });
  };

  const deleteDynamicSupplyOrder = (id: number) => {
    setDynamicSupplyOrderList((dynamicSupplyOrderList: any) =>
      dynamicSupplyOrderList.filter((x: any) => x.id !== id),
    );
  };

  const addDynamicFields = (info: IFields[], prefix: string) => {
    if (info.length) {
      const filtered = info.map(item =>
        Object.fromEntries(Object.entries(item).filter(([key]) => key.startsWith(`${prefix}_`))),
      );
      const prefixUsernameCount = filtered.reduce((count, obj) => {
        const keysWithUsername = Object.keys(obj).filter(key => key.includes('username'));
        return count + keysWithUsername.length;
      }, 0);
      const tempDynamicList = [];
      if (prefixUsernameCount > 1) {
        for (let ind = 0; ind < prefixUsernameCount - 1; ind += 1) {
          tempDynamicList.push({
            id: ind + 1,
          });
        }
        if (prefix === loginsAccountsPrefixEnum.SITE) {
          setDynamicSiteList(tempDynamicList);
        }
        if (prefix === loginsAccountsPrefixEnum.FOUNDATIONS) {
          setDynamicFoundationList(tempDynamicList);
        }
        if (prefix === loginsAccountsPrefixEnum.SUPPLY_ORDER) {
          setDynamicSupplyOrderList(tempDynamicList);
        }
      }
    }
  };

  const addDynamicFieldsData = (info: IFields[], prefix: string) => {
    const filteredData = info.map(item =>
      Object.fromEntries(
        Object.entries(item).filter(([key]) => key.startsWith(`${prefix}_`) || key === 'id'),
      ),
    );
    const prefixUsernameCount = filteredData.reduce((count, obj) => {
      const keysWithUsername = Object.keys(obj).filter(key => key.includes('username'));
      if (keysWithUsername && keysWithUsername.length) {
        return count + keysWithUsername.length;
      }
      return count;
    }, 0);
    let siteCounter = 0;
    let foundationsCounter = 0;
    let supplyOrderCounter = 0;
    filteredData.map(t => {
      const { id, ...fieldsName } = t;
      const processedTypes: string[] = [];
      const fieldsNameList = Object.keys(fieldsName);
      for (const key of fieldsNameList) {
        const [type, typeValue, name] = key.split('_');
        if (!processedTypes.includes(`${type}_${typeValue}`)) {
          processedTypes.push(`${type}_${typeValue}`);
          if (type === loginsAccountsPrefixEnum.SITE) {
            setFieldValue(`${LaDynamicPairFieldsEnum.SITE}_${siteCounter}`, typeValue);
            setFieldValue(
              `${LaDynamicPairFieldsEnum.SITE_USERNAME}_${siteCounter}`,
              t[`${type}_${typeValue}_username`],
            );
            setFieldValue(
              `${LaDynamicPairFieldsEnum.SITE_PASSWORD}_${siteCounter}`,
              t[`${type}_${typeValue}_password`],
            );
            updateSiteIdMapper([{ dbId: id, counter: siteCounter }]);
            siteCounter += 1;
          }
          if (type === loginsAccountsPrefixEnum.FOUNDATIONS) {
            setFieldValue(`${LaDynamicPairFieldsEnum.FOUNDATION}_${foundationsCounter}`, typeValue);
            setFieldValue(
              `${LaDynamicPairFieldsEnum.FOUNDATION_USERNAME}_${foundationsCounter}`,
              t[`${type}_${typeValue}_username`],
            );
            setFieldValue(
              `${LaDynamicPairFieldsEnum.FOUNDATION_PASSWORD}_${foundationsCounter}`,
              t[`${type}_${typeValue}_password`],
            );
            updateFoundationsIdMapper([{ dbId: id, counter: foundationsCounter }]);
            foundationsCounter += 1;
          }
          if (type === loginsAccountsPrefixEnum.SUPPLY_ORDER) {
            setFieldValue(
              `${LaDynamicPairFieldsEnum.SUPPLY_ORDER}_${supplyOrderCounter}`,
              typeValue,
            );
            setFieldValue(
              `${LaDynamicPairFieldsEnum.SUPPLY_ORDER_USERNAME}_${supplyOrderCounter}`,
              t[`${type}_${typeValue}_username`],
            );
            setFieldValue(
              `${LaDynamicPairFieldsEnum.SUPPLY_ORDER_PASSWORD}_${supplyOrderCounter}`,
              t[`${type}_${typeValue}_password`],
            );
            updateSupplyOrderIdMapper([{ dbId: id, counter: supplyOrderCounter }]);
            supplyOrderCounter += 1;
          }
        }
      }
    });
  };

  React.useEffect(() => {
    if (fieldsInfo.length) {
      addDynamicFields(fieldsInfo, loginsAccountsPrefixEnum.SITE);
      addDynamicFields(fieldsInfo, loginsAccountsPrefixEnum.FOUNDATIONS);
      addDynamicFields(fieldsInfo, loginsAccountsPrefixEnum.SUPPLY_ORDER);
    }
  }, [fieldsInfo]);

  React.useEffect(() => {
    if (fieldsInfo.length) {
      addDynamicFieldsData(fieldsInfo, loginsAccountsPrefixEnum.SITE);
      addDynamicFieldsData(fieldsInfo, loginsAccountsPrefixEnum.FOUNDATIONS);
      addDynamicFieldsData(fieldsInfo, loginsAccountsPrefixEnum.SUPPLY_ORDER);
    }
  }, [fieldsInfo]);

  React.useEffect(() => {
    updateDynamicSiteList(dynamicSiteList);
  }, [dynamicSiteList]);

  React.useEffect(() => {
    updateDynamicFoundationList(dynamicFoundationList);
  }, [dynamicFoundationList]);

  React.useEffect(() => {
    updateDynamicSupplyOrderList(dynamicSupplyOrderList);
  }, [dynamicSupplyOrderList]);

  return (
    <Grid item xs={12}>
      <Accordion className={classes.accordionheader}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon className={classes.expandIconColor} />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography>
            <Tooltip title={formTitle} placement="right-end">
              <span className={classes.accordionTitle}>{`${formTitle}`}</span>
            </Tooltip>
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.AccordionDetailsForm}>
          <Grid container spacing={3}>
            <Grid item xs={6} className={classes.subSectionTitle}>
              <Typography>
                <span>Site</span>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Grid container justifyContent="flex-end" className={classes.dynamicButtons}>
                <Button onClick={() => addDynamicSite()} variant="outlined">
                  <span>+ SITE</span>
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={3}>
              <Field
                name="la_site_0"
                label="Site"
                component={renderDropdown}
                validate={[]}
                fields={SITE_DROPDOWN}
                width="100%"
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="la_site_username_0"
                label="Username"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="la_site_password_0"
                label="Password"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
          </Grid>
          {(dynamicSiteList || []).map((t: any) => {
            if (t.id > 0) {
              return (
                <Grid container spacing={3} className={classes.rowMarginTop}>
                  <Grid item xs={3}>
                    <Field
                      name={`la_site_${t.id}`}
                      label="Site"
                      component={renderDropdown}
                      validate={[]}
                      fields={SITE_DROPDOWN}
                      width="100%"
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Field
                      name={`la_site_username_${t.id}`}
                      label="Username"
                      component={renderTextField}
                      validate={[]}
                      width="100%"
                      cpInputProperties={{
                        maxLength: MAX_LENGTH_INPUT,
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Field
                      name={`la_site_password_${t.id}`}
                      label="Password"
                      component={renderTextField}
                      validate={[]}
                      width="100%"
                      cpInputProperties={{
                        maxLength: MAX_LENGTH_INPUT,
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={3}
                    className={classes.centerIcon}
                    key={`la_site_delete_icon_${t.id}`}
                    onClick={() => {
                      deleteDynamicSite(t.id);
                    }}
                    width="100%"
                  >
                    <CloseIcon />
                  </Grid>
                </Grid>
              );
            }
            return '';
          })}
          <Grid item xs={12} className={classes.dividerLine}>
            <Divider />
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={6} className={classes.subSectionTitle}>
              <Typography>
                <span>Foundations</span>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Grid container justifyContent="flex-end" className={classes.dynamicButtons}>
                <Button onClick={() => addDynamicFoundation()} variant="outlined">
                  <span>+ FOUNDATION</span>
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={3}>
              <Field
                name="la_foundation_0"
                label="Foundation"
                component={renderDropdown}
                validate={[]}
                fields={FOUNDATIONS_DROPDOWN}
                width="100%"
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="la_foundation_username_0"
                label="Username"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="la_foundation_password_0"
                label="Password"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
          </Grid>
          {(dynamicFoundationList || []).map((t: any) => {
            if (t.id > 0) {
              return (
                <Grid container spacing={3} className={classes.rowMarginTop}>
                  <Grid item xs={3}>
                    <Field
                      name={`la_foundation_${t.id}`}
                      label="Foundation"
                      component={renderDropdown}
                      validate={[]}
                      fields={FOUNDATIONS_DROPDOWN}
                      width="100%"
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Field
                      name={`la_foundation_username_${t.id}`}
                      label="Username"
                      component={renderTextField}
                      validate={[]}
                      width="100%"
                      cpInputProperties={{
                        maxLength: MAX_LENGTH_INPUT,
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Field
                      name={`la_foundation_password_${t.id}`}
                      label="Password"
                      component={renderTextField}
                      validate={[]}
                      width="100%"
                      cpInputProperties={{
                        maxLength: MAX_LENGTH_INPUT,
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={3}
                    className={classes.centerIcon}
                    key={`la_foundation_delete_icon_${t.id}`}
                    onClick={() => {
                      deleteDynamicFoundation(t.id);
                    }}
                    width="100%"
                  >
                    <CloseIcon />
                  </Grid>
                </Grid>
              );
            }
            return '';
          })}
          <Grid item xs={12} className={classes.dividerLine}>
            <Divider />
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={6} className={classes.subSectionTitle}>
              <Typography>
                <span>Supply Orders</span>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Grid container justifyContent="flex-end" className={classes.dynamicButtons}>
                <Button onClick={() => addDynamicSupplyOrder()} variant="outlined">
                  <span>+ Supply Orders</span>
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={3}>
              <Field
                name="la_supply_order_0"
                label="Supply Order"
                component={renderDropdown}
                validate={[]}
                fields={SUPPLY_ORDER_DROPDOWN}
                width="100%"
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="la_supply_order_username_0"
                label="Username"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="la_supply_order_password_0"
                label="Password"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
          </Grid>
          {(dynamicSupplyOrderList || []).map((t: any) => {
            if (t.id > 0) {
              return (
                <Grid container spacing={3} className={classes.rowMarginTop}>
                  <Grid item xs={3}>
                    <Field
                      name={`la_supply_order_${t.id}`}
                      label="Supply Order"
                      component={renderDropdown}
                      validate={[]}
                      fields={SUPPLY_ORDER_DROPDOWN}
                      width="100%"
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Field
                      name={`la_supply_order_username_${t.id}`}
                      label="Username"
                      component={renderTextField}
                      validate={[]}
                      width="100%"
                      cpInputProperties={{
                        maxLength: MAX_LENGTH_INPUT,
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Field
                      name={`la_supply_order_password_${t.id}`}
                      label="Password"
                      component={renderTextField}
                      validate={[]}
                      width="100%"
                      cpInputProperties={{
                        maxLength: MAX_LENGTH_INPUT,
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={3}
                    className={classes.centerIcon}
                    key={`la_supply_order_delete_icon_${t.id}`}
                    onClick={() => {
                      deleteDynamicSupplyOrder(t.id);
                    }}
                    width="100%"
                  >
                    <CloseIcon />
                  </Grid>
                </Grid>
              );
            }
            return '';
          })}
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};
